import { atom } from 'jotai'
import type { PrimitiveAtom } from 'jotai'
import type { GeometryObject } from 'geojson'
import type { GeometryActions } from '@/js/editor/hooks/useControls.ts'
import { poisAtom } from '@/js/editor/state/mapAtoms.ts'

type EditorUiState = {
    activeGeometryIndex: number | null
    activeGeometry: GeometryObject | null
    highlightedGeometry: GeometryObject | null
    highlightedPoiId: string | number | null
    isSettingCenter: boolean
    activeGeometryTool: GeometryActions | null
}

const initialEditorUiState: EditorUiState = {
    activeGeometryIndex: null,
    activeGeometry: null,
    highlightedGeometry: null,
    highlightedPoiId: null,
    isSettingCenter: false,
    activeGeometryTool: null,
}

const editorUiStateAtom: PrimitiveAtom<EditorUiState> = atom<EditorUiState>(initialEditorUiState)

export const activeGeometryIndexAtom = atom((get) => get(editorUiStateAtom).activeGeometryIndex)
export const activeGeometryAtom = atom((get) => get(editorUiStateAtom).activeGeometry)
export const highlightedGeometryAtom = atom((get) => get(editorUiStateAtom).highlightedGeometry)
export const highlightedPoiIdAtom = atom((get) => get(editorUiStateAtom).highlightedPoiId)
export const isSettingCenterAtom = atom((get) => get(editorUiStateAtom).isSettingCenter)
export const activeGeometryToolAtom = atom((get) => get(editorUiStateAtom).activeGeometryTool)

export const highlightedPoiAtom = atom((get) => {
    const highlightedPoiId = get(highlightedPoiIdAtom)

    if (highlightedPoiId === null) {
        return null
    }

    return get(poisAtom).find((poi) => String(poi.id) === String(highlightedPoiId)) ?? null
})

export const setHighlightedPoiAtom = atom(null, (_get, set, poiId: string | number | null) => {
    set(editorUiStateAtom, (state) => ({
        ...state,
        highlightedPoiId: poiId,
    }))
})

export const setActiveGeometryAtom = atom(
    null,
    (
        _get,
        set,
        {
            geometry,
            index,
        }: {
            geometry: GeometryObject | null
            index: number | null
        }
    ) => {
        set(editorUiStateAtom, (state) => ({
            ...state,
            activeGeometry: geometry,
            activeGeometryIndex: index,
        }))
    }
)

export const clearActiveGeometryAtom = atom(null, (_get, set) => {
    set(editorUiStateAtom, (state) => ({
        ...state,
        activeGeometry: null,
        activeGeometryIndex: null,
    }))
})

export const setHighlightedGeometryAtom = atom(null, (_get, set, geometry: GeometryObject | null) => {
    set(editorUiStateAtom, (state) => ({
        ...state,
        highlightedGeometry: geometry,
    }))
})

export const setHighlightedPoiIdAtom = atom(null, (_get, set, poiId: string | number | null) => {
    set(editorUiStateAtom, (state) => ({
        ...state,
        highlightedPoiId: poiId,
    }))
})

export const setSettingCenterAtom = atom(null, (_get, set, value: boolean) => {
    set(editorUiStateAtom, (state) => ({
        ...state,
        isSettingCenter: value,
    }))
})

export const setActiveGeometryToolAtom = atom(null, (_get, set, tool: GeometryActions | null) => {
    set(editorUiStateAtom, (state) => ({
        ...state,
        activeGeometryTool: tool,
    }))
})

export const resetEditorUiAtom = atom(null, (_get, set) => {
    set(editorUiStateAtom, initialEditorUiState)
})
